import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import SellerLayout from "@/components/SellerLayout";
import { PageHeader, DataTable, EmptyState, StatCard } from "@/components/ui-system";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Wallet, Clock, CheckCircle2, Landmark, Banknote, AlertTriangle } from "lucide-react";
import { formatToIST } from "@/utils/dateUtils";

const PAGE_SIZE = 50;
const TABS = [
  { id: "awaiting", label: "Awaiting" },
  { id: "collected", label: "Pending Payout" },
  { id: "paid", label: "Paid" },
  { id: "all", label: "All" },
];

const inr = (n) =>
  `₹${Number(n ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

const errDetail = (error, fallback) => {
  const d = error?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d) && d[0]?.msg) return d[0].msg;
  return error?.response?.data?.message || fallback;
};

const CodBadge = ({ status }) => {
  const map = {
    awaiting: { cls: "bg-slate-100 text-slate-600 border-slate-200", label: "Awaiting" },
    collected: { cls: "bg-amber-50 text-amber-700 border-amber-200", label: "Pending payout" },
    paid: { cls: "bg-green-50 text-green-700 border-green-200", label: "Paid" },
  };
  const m = map[status] || map.awaiting;
  return <Badge variant="outline" className={m.cls}>{m.label}</Badge>;
};

const B2CCodReconciliation = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("all");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notEnabled, setNotEnabled] = useState(false);

  const fetchCod = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: PAGE_SIZE };
      if (activeTab !== "all") params.status_group = activeTab;
      const { data: res } = await axios.get(`${API}/b2c/seller/cod`, { params });
      setData(res);
      setNotEnabled(false);
    } catch (e) {
      if (e?.response?.status === 422) setNotEnabled(true);
      else toast.error(errDetail(e, "Failed to load COD reconciliation"));
    } finally {
      setLoading(false);
    }
  }, [page, activeTab]);

  useEffect(() => { fetchCod(); }, [fetchCod]);
  useEffect(() => { setPage(1); }, [activeTab]);

  const summary = data?.summary || {};
  const bank = data?.bank;
  const orders = data?.orders || { items: [], total: 0 };
  const payouts = data?.payouts || [];
  const totalPages = Math.max(1, Math.ceil((orders.total || 0) / PAGE_SIZE));

  const columns = [
    { key: "order", header: "Reference #", primary: true,
      cell: (o) => (
        <button onClick={() => navigate(`/seller/b2c/orders/${o.id}`)}
          className="flex flex-col items-start leading-tight text-left">
          <span className="text-sm font-medium text-brand hover:underline">{o.reference_number || "-"}</span>
          <span className="text-xs text-slate-400">{o.order_number}</span>
        </button>
      ) },
    { key: "awb", header: "AWB", hideOnMobile: true,
      cell: (o) => o.awb ? <span className="text-sm font-mono text-slate-700">{o.awb}</span> : <span className="text-slate-400">-</span> },
    { key: "customer", header: "Customer", cell: (o) => <span className="text-sm text-slate-700">{o.buyer_name || "-"}</span> },
    { key: "cod", header: "COD amount", align: "right",
      cell: (o) => <span className="text-sm font-semibold tabular-nums text-slate-800">{inr(o.cod_amount)}</span> },
    { key: "status", header: "COD status", cell: (o) => <CodBadge status={o.cod_status} /> },
    { key: "payout", header: "Payout", hideOnMobile: true,
      cell: (o) => o.cod_payout_number
        ? <span className="text-xs font-mono text-slate-600">{o.cod_payout_number}</span>
        : <span className="text-slate-400">-</span> },
  ];

  return (
    <SellerLayout user={user} onLogout={onLogout}>
      <div className="space-y-6 w-full">
        <PageHeader title="COD Reconciliation" subtitle="COD we collect on your shipments, transferred to your bank" />

        {notEnabled ? (
          <Card className="border-0 shadow-sm">
            <CardContent className="p-0">
              <EmptyState icon={Wallet} title="B2C is not enabled yet"
                description="Your account doesn't have a B2C plan configured. Ask your account manager to enable B2C shipping." />
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <StatCard label="Awaiting collection" value={inr(summary.awaiting?.amount)} icon={Clock} accent="neutral"
                loading={loading} trend={`${summary.awaiting?.count ?? 0} orders`} />
              <StatCard label="Pending payout" value={inr(summary.collected?.amount)} icon={Banknote} accent="warning"
                loading={loading} trend={`${summary.collected?.count ?? 0} collected`} />
              <StatCard label="Paid to you" value={inr(summary.paid?.amount)} icon={CheckCircle2} accent="success"
                loading={loading} trend={`${summary.paid?.count ?? 0} orders`} />
            </div>

            {/* Bank on file */}
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Landmark className="w-4 h-4 text-brand" /> Payout bank account
                </CardTitle>
              </CardHeader>
              <CardContent>
                {bank ? (
                  <div className="flex flex-wrap items-center gap-x-8 gap-y-2 text-sm">
                    <div><p className="text-xs text-slate-500">Account holder</p><p className="font-medium text-slate-800">{bank.account_holder_name}</p></div>
                    <div><p className="text-xs text-slate-500">Account</p><p className="font-mono font-medium text-slate-800">{bank.account_number_masked}</p></div>
                    <div><p className="text-xs text-slate-500">IFSC</p><p className="font-mono font-medium text-slate-800">{bank.ifsc}</p></div>
                    <div><p className="text-xs text-slate-500">Bank</p><p className="font-medium text-slate-800">{bank.bank_name}</p></div>
                  </div>
                ) : (
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between rounded-lg border border-dashed border-amber-200 bg-amber-50/60 p-4">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5" />
                      <p className="text-sm text-amber-800">No bank account on file — add it in KYC so we can pay out your COD.</p>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => navigate("/seller/b2c/kyc")}>Add bank details</Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* COD orders */}
            <div className="flex gap-2 border-b border-slate-200 bg-white px-2 sm:px-6 rounded-t-lg shadow-sm overflow-x-auto">
              {TABS.map((t) => (
                <button key={t.id} onClick={() => setActiveTab(t.id)}
                  className={`px-3 sm:px-5 py-3 text-xs sm:text-sm font-medium transition-colors border-b-2 whitespace-nowrap flex-shrink-0 ${
                    activeTab === t.id ? "border-brand text-brand" : "border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-300"
                  }`}>{t.label}</button>
              ))}
            </div>
            <DataTable
              columns={columns}
              data={orders.items}
              rowKey={(o) => o.id}
              loading={loading}
              empty={<EmptyState icon={Wallet} title="No COD orders" description="COD orders appear here once you ship a cash-on-delivery parcel." />}
              pagination={totalPages > 1 ? { page, pages: totalPages, total: orders.total, pageSize: PAGE_SIZE, onPageChange: setPage } : undefined}
            />

            {/* Payout history */}
            {payouts.length > 0 && (
              <Card className="border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Banknote className="w-4 h-4 text-brand" /> Payout history
                  </CardTitle>
                </CardHeader>
                <CardContent className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-slate-500 border-b border-slate-100">
                        <th className="py-2 pr-4 font-medium">Payout</th>
                        <th className="py-2 pr-4 font-medium">Date</th>
                        <th className="py-2 pr-4 font-medium">Orders</th>
                        <th className="py-2 pr-4 font-medium">UTR</th>
                        <th className="py-2 pr-4 font-medium text-right">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payouts.map((p) => (
                        <tr key={p.id} className="border-b border-slate-50 last:border-0">
                          <td className="py-2 pr-4 font-mono text-slate-700">{p.payout_number}</td>
                          <td className="py-2 pr-4 text-slate-600 whitespace-nowrap">{formatToIST(p.paid_at)}</td>
                          <td className="py-2 pr-4 text-slate-600">{p.order_count}</td>
                          <td className="py-2 pr-4 font-mono text-slate-600">{p.utr}</td>
                          <td className="py-2 pr-4 text-right font-semibold tabular-nums text-slate-800">{inr(p.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </SellerLayout>
  );
};

export default B2CCodReconciliation;
